// @react
import React, { ReactElement, useContext, useEffect, useState } from 'react'
// @types
import {
  GetConstantsQueryResult,
  GetUserQueryResult,
  UserJobUpdateMutation,
  UserJobUpdateMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { WorkFormValues } from 'modules/common/forms/types/work'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @libraries
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { DevTool } from '@hookform/devtools'
import { vsprintf } from 'sprintf-js'
import strings from 'lib/constants/strings'
// @graphQL
import { useMutation } from '@apollo/client'
import client from 'graphql/Apollo/apolloClient'
import { USER_JOB_UPDATE } from 'graphql/mutations/user.mutation'
// @context
import { PersonalInformationContext } from 'modules/profile/context'
import { SnackbarContext } from 'modules/common/context/snackbar'
// @components
import Grid from '@bit/atd.web.grid'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import EditAction from 'modules/profile/components/EditAction'
import EditingAction from 'modules/profile/components/EditingAction'
import WorkPublicFields from 'modules/common/forms/fields/work/WorkPublic'
import WorkHiddenFields from 'modules/common/forms/fields/work/WorkHidden'
import { validationSchema } from 'modules/common/forms/validations/work'

type PropsType = {
  data?: GetUserQueryResult['data']
  constants?: GetConstantsQueryResult['data']
  isLoading: boolean
}

const JobForm = ({
  data: userData,
  constants,
  isLoading,
}: PropsType): ReactElement => {
  /****************************************
   * STATE
   *****************************************/

  const [editing, setEditing] = useState<boolean>(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)

  /****************************************
   * HOOKS
   *****************************************/

  const snackbar = useContext(SnackbarContext)
  const profileInformation = useContext(PersonalInformationContext)
  const formMethods = useForm<WorkFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      industry: '',
      role: '',
      jobTitle: '',
      companyName: '',
      companySize: '',
      trainingBudget: '',
      directReportCount: '',
    },
  })
  const { control, formState, reset } = formMethods
  const { errors } = formState
  const job = userData?.user?.job
  useEffect(() => {
    reset({
      industry: job?.jobIndustry || '',
      role: job?.jobFunctionCode || '',
      jobTitle: job?.jobTitle || '',
      companyName: job?.companyName || '',
      companySize: job?.companySize || '',
      trainingBudget: job?.trainingBudget || '',
      directReportCount: job?.directReportCount || '',
    })
  }, [reset, job])

  /****************************************
   * GQL HOOKS
   *****************************************/

  const [updateUserJob] = useMutation<
    UserJobUpdateMutation,
    UserJobUpdateMutationVariables
  >(USER_JOB_UPDATE)

  /****************************************
   * FUNCTIONS
   *****************************************/

  const onEditHandler = () => {
    profileInformation.setEditingSection('job')
    setEditing(true)
  }

  const onCancelHandler = () => {
    profileInformation.setEditingSection(null)
    setEditing(false)
    formMethods.clearErrors()
    reset()
  }

  const onSubmitHandler = async (data: WorkFormValues) => {
    setIsSubmitting(true)
    await userJobUpdateHandler(data)
    setIsSubmitting(false)
    profileInformation.setEditingSection(null)
    setEditing(false)
  }

  const userJobUpdateHandler = async (data: WorkFormValues) => {
    const { errors: mutationErrors } = await updateUserJob({
      variables: {
        input: {
          jobFunctionCode: data.role,
          jobTitle: data.jobTitle,
          companyName: data.companyName,
          companySize: data.companySize,
          trainingBudget: data.trainingBudget,
          directReportCount: data.directReportCount,
          jobIndustry: data.industry,
        },
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Work',
        ]),
        show: true,
      })
      if (userData?.user) {
        client.cache.modify({
          id: client.cache.identify(userData.user), // we can asset the user is known
          fields: {
            job(cachedData) {
              return {
                ...cachedData,
                jobFunctionCode: data.role,
                jobTitle: data.jobTitle,
                companyName: data.companyName,
                companySize: data.companySize,
                trainingBudget: data.trainingBudget,
                directReportCount: data.directReportCount,
                jobIndustry: data.industry,
              }
            },
          },
        })
      }
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
  }

  /****************************************
   * RENDER
   *****************************************/

  const form = (
    <>
      {process.env.DEBUG !== '1' && <DevTool control={control} />}

      <form>
        <Grid container spacing={4}>
          <WorkPublicFields
            editing={editing}
            userData={userData}
            constantData={constants}
            control={control}
            errors={errors}
          />

          <WorkHiddenFields
            editing={editing}
            constantData={constants}
            control={control}
          />
        </Grid>
      </form>
    </>
  )

  const box = (content: ReactElement): ReactElement => {
    return (
      <Box
        header={{
          title: !editing ? 'Work' : 'Editing Work',
          actions: !editing ? (
            <EditAction disabled={isLoading} onClick={() => onEditHandler()} />
          ) : (
            <EditingAction
              bottom
              isSubmitting={isMutationSubmitting}
              onCancel={() => onCancelHandler()}
              onSubmit={formMethods.handleSubmit((data) =>
                onSubmitHandler(data),
              )}
            />
          ),
        }}
      >
        {content}
      </Box>
    )
  }

  return !isLoading ? box(form) : box(<SkeletonLoader />)
}

export default JobForm
